import { sprinkles } from '@design-system/sprinkles.css';
import { bgColorsVars } from '@design-system/vars.css';
import { Slider, LinearProgress } from '@mui/material';
import { AddFromLibaryVM } from '../../view-models/addFromLibrary.viewModel';
import { Typography } from '@design-system/components/typography/typography';
import { Input } from '@design-system/components/input/input';
import { clsx } from 'clsx';
import { getVideoTimeFormat } from '../../../influencer/videos/videos.helpers';
import { useRef } from 'react';
import { IconButton } from '@design-system/components/button/iconButton';
import {
  faClose,
  faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getFileNameWihoutExtension } from '../../helpers';

export const UploadStep = ({
  step,
  progress,
  sliderChange,
  dispatch,
  fileName,
  duration,
  thumbnailTimestamp,
  uploadSizeWarning,
}: {
  step: AddFromLibaryVM['upload']['step'];
  progress: number;
  sliderChange: (_event: Event | null, newValue: number | number[]) => void;
  dispatch: AddFromLibaryVM['dispatch'];
  fileName: string;
  duration: number;
  thumbnailTimestamp: number;
  uploadSizeWarning?: boolean;
}) => {
  switch (step) {
    case 'Name':
      return <NameEdit fileName={fileName} dispatch={dispatch} />;
    case 'Thumbnail':
      return (
        <Thumbnail
          thumbnailTimestamp={thumbnailTimestamp}
          duration={duration}
          sliderChange={sliderChange}
        />
      );
    case 'Uploading':
      return (
        <UploadProgress
          progress={progress}
          fileName={getFileNameWihoutExtension(fileName)}
          cancelUpload={() => dispatch('cancelUpload')}
          uploadSizeWarning={uploadSizeWarning}
        />
      );
    case 'Processing':
      return <Processing fileName={getFileNameWihoutExtension(fileName)} />;
    default:
      return null;
  }
};

const getFileNameAndExtension = (fileName: string) => {
  const divider = fileName.lastIndexOf('.');
  return [
    fileName.substring(0, divider),
    fileName.substring(divider + 1, fileName.length),
  ];
};

const NameEdit = ({
  dispatch,
  fileName,
}: {
  dispatch: AddFromLibaryVM['dispatch'];
  fileName: string;
}) => {
  const inputEl = useRef<HTMLInputElement>(null);
  const [name, extension] = getFileNameAndExtension(fileName);
  function handleFocus() {
    if (inputEl.current !== null) {
      inputEl.current.select();
    }
  }

  return (
    <Input
      label="Title"
      ref={inputEl}
      required
      value={name}
      onChange={(ev) => dispatch('editName', `${ev.target.value}.${extension}`)}
      onFocus={handleFocus}
    />
  );
};

const Thumbnail = ({
  duration,
  sliderChange,
  thumbnailTimestamp,
}: {
  duration: number;
  sliderChange: (_event: Event | null, newValue: number | number[]) => void;
  thumbnailTimestamp: number;
}) => (
  <div className="flex flex-col justify-center items-center pt-4">
    <Typography variant="body" className={sprinkles({ textAlign: 'center' })}>
      {getVideoTimeFormat(thumbnailTimestamp)}/{getVideoTimeFormat(duration)}
    </Typography>
    <Slider
      value={thumbnailTimestamp}
      max={duration}
      step={0.01}
      onChange={sliderChange}
    />
  </div>
);

const UploadProgress = ({
  progress,
  fileName,
  cancelUpload,
  uploadSizeWarning,
}: {
  progress: number;
  fileName: string;
  cancelUpload: () => void;
  uploadSizeWarning?: boolean;
}) => (
  <div className="flex gap-4 items-center justify-between pt-6">
    <div
      className={clsx(
        'w-full',
        sprinkles({
          display: 'flex',
          flexDirection: 'column',
          gap: 'm',
        }),
      )}
    >
      <Typography variant="bodyLarge">{fileName}</Typography>
      <div className="flex w-full gap-4 items-center">
        <div className="w-full mr-1 inline">
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              '&.MuiLinearProgress-root': {
                width: '100%',
                background: bgColorsVars['N-20'],
              },
              '&.MuiLinearProgress-root > .MuiLinearProgress-bar1Determinate': {
                background: bgColorsVars['P-80'],
              },
            }}
          />
        </div>
        <div>
          <Typography
            variant="body"
            className={sprinkles({ color: 'P-80' })}
          >{`${Math.round(progress)}%`}</Typography>
        </div>
      </div>
      {uploadSizeWarning ? (
        <div className="flex gap-1">
          <FontAwesomeIcon
            icon={faTriangleExclamation}
            className={sprinkles({ color: 'WARNING-900', fontSize: 's' })}
          />
          <Typography
            variant="body"
            size="small"
            className={sprinkles({ color: 'N-60' })}
          >
            Upload takes longer due to file size
          </Typography>
        </div>
      ) : null}
    </div>
    <IconButton
      icon={faClose}
      onClick={cancelUpload}
      variant="text"
      color="neutral"
    />
  </div>
);

const Processing = ({ fileName }: { fileName: string }) => (
  <div className="flex flex-col">
    <Typography variant="bodyLarge">{fileName}</Typography>
    <Typography variant="bodyLarge">Processing...</Typography>
  </div>
);
